import { Button, Flex, InfoIcon, ProfilePhoto, Text } from "@minervaproject/fds";
import clsx from "clsx";

import { MobileCardHorizontalLine } from "components/shared/mobile_components";
import { PollVisibility } from "components/stage/poll_session_pane/poll_visibility";
import { useAppDispatch, useAppSelector } from "hooks/redux_hooks";
import { useIsMobile } from "hooks/use_is_mobile";
import {
  addUserToParticipantBarAndToBestPaneOnStage,
  featurePollResponse,
  hideFeaturedPollResponse,
} from "shared/reducers/classroom";
import { FeaturedPollResponseType, PollResponseType } from "shared/types";
import { UserType } from "shared/types/user";
import { localSelectors } from "store";
import { computeInitials, longDisplayName, profilePhotoUrl } from "utils/user";

function LongFormPollResponse({
  pollResponse,
  user,
  canFeature,
  onFeature,
  isLast,
  noMargin = false,
  scroll = false,
  isMobile = false,
}: {
  pollResponse: PollResponseType | FeaturedPollResponseType;
  user: UserType;
  canFeature: boolean;
  onFeature?: () => void;
  noMargin?: boolean;
  scroll?: boolean;
  isLast: boolean;
  isMobile?: boolean;
}) {
  const Photo = (
    <ProfilePhoto
      className="mr3"
      size={isMobile ? "md" : "lg"}
      src={profilePhotoUrl(user)}
      initials={computeInitials(user)}
    />
  );

  const Name = (
    <Text kind="body-s" bold color="blackTint40" className="flex-none">
      {longDisplayName(user)}
    </Text>
  );

  const Response = (
    <pre
      className={clsx("wrap-words body", { "vertical-scroll": scroll })}
      style={{
        color:
          "is_draft" in pollResponse && pollResponse.is_draft
            ? "var(--black-tint-40)"
            : "var(--black-tint-10)",
        whiteSpace: "pre-wrap",
      }}
      data-testid="long-form-response"
    >
      {pollResponse.response}
    </pre>
  );

  if (isMobile) {
    return (
      <div className="overflow-hidden">
        <Flex className="justify-between mb2">
          <Flex className="self-start items-center">
            {Photo}
            {Name}
          </Flex>
          {canFeature && (
            <button className="text-button mt1" type="button" onClick={onFeature}>
              <span className="bold">Feature</span>
            </button>
          )}
        </Flex>
        {Response}
        {!isLast && <MobileCardHorizontalLine />}
      </div>
    );
  }

  return (
    <div className={clsx("flex", { "flex-none": !scroll, mb5: !noMargin })}>
      {Photo}
      <div className="flex flex-column">
        {Name}
        {Response}
        {canFeature && (
          <button
            className="text-button mt1 self-start"
            type="button"
            onClick={onFeature}
            data-testid="feature-response-button"
          >
            Feature response
          </button>
        )}
      </div>
    </div>
  );
}

const EMPTY_POLL_RESPONSE_CONTENT = "This student was present, but did not complete the poll.";

function LongFormPollResponses({
  pollSessionId,
  pollResponses,
  users,
  canFeaturePollResponse,
  featuredPollResponse,
  canSeeAllPollResponses,
  isPresentationView,
}: {
  pollSessionId: string;
  pollResponses: PollResponseType[];
  users: { [id: string]: UserType };
  canFeaturePollResponse: boolean;
  featuredPollResponse?: FeaturedPollResponseType;
  canSeeAllPollResponses?: boolean;
  isPresentationView?: boolean;
}) {
  const dispatch = useAppDispatch();
  const userId = useAppSelector(localSelectors.selectCurrentUserId());
  const isMobile = useIsMobile();
  // TODO If a student doesn't submit the poll intentionally, it doesn't register as an empty poll
  const emptyPollResponses = pollResponses.filter(
    (pollResponse) => pollResponse.response === EMPTY_POLL_RESPONSE_CONTENT,
  );
  const nonEmptyPollResponses = pollResponses.filter(
    (pollResponse) => pollResponse.response !== EMPTY_POLL_RESPONSE_CONTENT,
  );
  const myPollResponse = pollResponses.find((pollResponse) => pollResponse.user_id === userId);

  const handleFeatureResponse = (pollResponse: PollResponseType) => {
    dispatch(
      featurePollResponse({
        pollResponseId: pollResponse.id,
        pollSessionId,
        response: pollResponse.response as string,
        userId: pollResponse.user_id,
      }),
    );
    dispatch(
      addUserToParticipantBarAndToBestPaneOnStage({
        userId: pollResponse.user_id,
        pinUserToStage: true,
      }),
    );
  };
  const stopFeaturing = () =>
    dispatch(hideFeaturedPollResponse({ pollResponseId: featuredPollResponse?.id }));

  if ((isMobile || isPresentationView) && (featuredPollResponse || !canSeeAllPollResponses)) {
    const pollResponse = featuredPollResponse || pollResponses.find((pr) => pr.user_id === userId);

    // If the poll has ended but the answers are not visible to students, we display nothing on
    // the presentation view (beyond the poll question that is displayed by a parent component).
    if (!pollResponse && isPresentationView) return null;
    if (!pollResponse && !featuredPollResponse)
      return <div>You have not submitted any answer.</div>;
    if (!pollResponse) return <div>Error displaying response</div>;

    return (
      <Flex auto direction="column">
        {canFeaturePollResponse && (
          <Flex className="mb4 justify-center items-center body-s bold">
            <Flex className="items-center">
              <InfoIcon width={16} />
              <span className="ml2 mr1">Featuring Response | </span>
              <Button kind="text" size="sm" onClick={stopFeaturing}>
                <span className="bold">Stop</span>
              </Button>
            </Flex>
          </Flex>
        )}
        <LongFormPollResponse
          pollResponse={pollResponse}
          user={users[pollResponse.user_id]}
          canFeature={false}
          isMobile={isMobile}
          isLast
        />
        {canFeaturePollResponse && (
          <Button kind="primary-mobile" className="mt4 w-full" onClick={stopFeaturing}>
            Stop Featuring to Class
          </Button>
        )}
      </Flex>
    );
  }

  return (
    <div className="flex flex-column pb4">
      {canSeeAllPollResponses ? (
        <>
          {nonEmptyPollResponses.map((pollResponse, index) => (
            <LongFormPollResponse
              key={pollResponse.id}
              pollResponse={pollResponse}
              user={users[pollResponse.user_id]}
              canFeature={canFeaturePollResponse}
              isLast={index === pollResponses.length - 1}
              isMobile={isMobile}
              onFeature={() => handleFeatureResponse(pollResponse)}
            />
          ))}
          {/* TODO Mobile - empty poll responses aren't displayed right currently */}
          {/* TODO Mobile - join this with general poll response format so we don't have to maintain double the styles */}
          {emptyPollResponses.length > 0 && (
            <div
              className="flex flex-column mt1 pt6"
              style={{ borderTop: "1px solid var(--black-tint-90)" }}
            >
              <h3 className="h3 text-black-tint-10">
                These students were present, but did not complete the poll:
              </h3>
              {emptyPollResponses.map((pollResponse) => {
                const user = users[pollResponse.user_id];
                return (
                  <div
                    key={pollResponse.id}
                    className="flex items-center flex-none mt5"
                    data-testid={`empty-response-${user.id}`}
                  >
                    <ProfilePhoto
                      className="mr3"
                      size="lg"
                      src={profilePhotoUrl(user)}
                      initials={computeInitials(user)}
                      style={{ opacity: "50%" }}
                    />
                    <Text kind="body-s" bold color="blackTint40">
                      {longDisplayName(user)}
                    </Text>
                  </div>
                );
              })}
            </div>
          )}
        </>
      ) : (
        <>
          <PollVisibility isStudentVisible={false} />
          {myPollResponse && (
            <div className="mt5">
              <LongFormPollResponse
                pollResponse={myPollResponse}
                user={users[myPollResponse.user_id]}
                canFeature={false}
                isLast
                isMobile={isMobile}
                onFeature={() => {}}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}

export { LongFormPollResponse, LongFormPollResponses };
